import { useState, useMemo, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  Search, Save, ExternalLink, Plus, Trash2, X,
  Building2, Users, Cpu, CheckSquare, Square, Sparkles,
  ChevronDown, ChevronUp,
} from 'lucide-react'
import { api, ApiError } from '@/lib/api/client'
import type {
  Prospect, ProspectContact, ProspectSourceSystem,
  RoaringHit, RoaringSearchResult, SourceSystem, CompanyStructureNode,
  LeadResearch, LeadResearchFinancial, LeadResearchNews,
} from '@/types/api'
import { Drawer } from '@/components/primitives/Drawer'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog'

// ─── Constants ────────────────────────────────────────────────────────────────

const LEGAL_GROUP_OPTIONS = [
  { value: '',                    label: 'All legal forms' },
  { value: 'Privat aktiebolag',   label: 'Privat aktiebolag (AB)' },
  { value: 'Publikt aktiebolag',  label: 'Publikt aktiebolag (Publ AB)' },
  { value: 'Handelsbolag',        label: 'Handelsbolag (HB)' },
  { value: 'Kommanditbolag',      label: 'Kommanditbolag (KB)' },
  { value: 'Enskild firma',       label: 'Enskild firma' },
]

const STATUS_OPTIONS = [
  { value: '100', label: 'Active (100)' },
  { value: '',    label: 'All statuses' },
]

const INCOME_RANGE_OPTIONS = [
  { value: '',          label: 'Any income' },
  { value: '0:99',      label: '< 100k SEK' },
  { value: '100:499',   label: '100k – 500k SEK' },
  { value: '500:999',   label: '500k – 1M SEK' },
  { value: '1000:4999', label: '1M – 5M SEK' },
  { value: '5000:9999', label: '5M – 10M SEK' },
  { value: '10000:',    label: '> 10M SEK' },
]

const SNI_CODES = [
  { code: '01', label: 'Crop and animal production, hunting' },
  { code: '02', label: 'Forestry and logging' },
  { code: '03', label: 'Fishing and aquaculture' },
  { code: '05', label: 'Mining of coal and lignite' },
  { code: '06', label: 'Extraction of crude petroleum and natural gas' },
  { code: '07', label: 'Mining of metal ores' },
  { code: '08', label: 'Other mining and quarrying' },
  { code: '09', label: 'Mining support service activities' },
  { code: '10', label: 'Manufacture of food products' },
  { code: '11', label: 'Manufacture of beverages' },
  { code: '12', label: 'Manufacture of tobacco products' },
  { code: '13', label: 'Manufacture of textiles' },
  { code: '14', label: 'Manufacture of wearing apparel' },
  { code: '15', label: 'Manufacture of leather and related products' },
  { code: '16', label: 'Manufacture of wood and wood products' },
  { code: '17', label: 'Manufacture of paper and paper products' },
  { code: '18', label: 'Printing and reproduction of recorded media' },
  { code: '19', label: 'Manufacture of coke and refined petroleum products' },
  { code: '20', label: 'Manufacture of chemicals and chemical products' },
  { code: '21', label: 'Manufacture of basic pharmaceutical products' },
  { code: '22', label: 'Manufacture of rubber and plastic products' },
  { code: '23', label: 'Manufacture of other non-metallic mineral products' },
  { code: '24', label: 'Manufacture of basic metals' },
  { code: '25', label: 'Manufacture of fabricated metal products' },
  { code: '26', label: 'Manufacture of computer, electronic and optical products' },
  { code: '27', label: 'Manufacture of electrical equipment' },
  { code: '28', label: 'Manufacture of machinery and equipment' },
  { code: '29', label: 'Manufacture of motor vehicles and trailers' },
  { code: '30', label: 'Manufacture of other transport equipment' },
  { code: '31', label: 'Manufacture of furniture' },
  { code: '32', label: 'Other manufacturing' },
  { code: '33', label: 'Repair and installation of machinery and equipment' },
  { code: '35', label: 'Electricity, gas, steam and air conditioning supply' },
  { code: '36', label: 'Water collection, treatment and supply' },
  { code: '37', label: 'Sewerage' },
  { code: '38', label: 'Waste collection, treatment and disposal' },
  { code: '39', label: 'Remediation and other waste management' },
  { code: '41', label: 'Construction of buildings' },
  { code: '42', label: 'Civil engineering' },
  { code: '43', label: 'Specialised construction activities' },
  { code: '45', label: 'Wholesale and retail trade of motor vehicles' },
  { code: '46', label: 'Wholesale trade, except of motor vehicles' },
  { code: '47', label: 'Retail trade, except of motor vehicles' },
  { code: '49', label: 'Land transport and transport via pipelines' },
  { code: '50', label: 'Water transport' },
  { code: '51', label: 'Air transport' },
  { code: '52', label: 'Warehousing and support for transportation' },
  { code: '53', label: 'Postal and courier activities' },
  { code: '55', label: 'Accommodation' },
  { code: '56', label: 'Food and beverage service activities' },
  { code: '58', label: 'Publishing activities' },
  { code: '59', label: 'Motion picture, video and television production' },
  { code: '60', label: 'Programming and broadcasting activities' },
  { code: '61', label: 'Telecommunications' },
  { code: '62', label: 'Computer programming and consultancy' },
  { code: '63', label: 'Information service activities' },
  { code: '64', label: 'Financial service activities' },
  { code: '65', label: 'Insurance, reinsurance and pension funding' },
  { code: '66', label: 'Activities auxiliary to financial services' },
  { code: '68', label: 'Real estate activities' },
  { code: '69', label: 'Legal and accounting activities' },
  { code: '70', label: 'Head offices and management consultancy' },
  { code: '71', label: 'Architectural and engineering activities' },
  { code: '72', label: 'Scientific research and development' },
  { code: '73', label: 'Advertising and market research' },
  { code: '74', label: 'Other professional, scientific and technical activities' },
  { code: '75', label: 'Veterinary activities' },
  { code: '77', label: 'Rental and leasing activities' },
  { code: '78', label: 'Employment activities' },
  { code: '79', label: 'Travel agency and tour operator' },
  { code: '80', label: 'Security and investigation activities' },
  { code: '81', label: 'Services to buildings and landscape' },
  { code: '82', label: 'Office administrative and business support' },
  { code: '84', label: 'Public administration and defence' },
  { code: '85', label: 'Education' },
  { code: '86', label: 'Human health activities' },
  { code: '87', label: 'Residential care activities' },
  { code: '88', label: 'Social work without accommodation' },
  { code: '90', label: 'Creative, arts and entertainment' },
  { code: '91', label: 'Libraries, archives and museums' },
  { code: '92', label: 'Gambling and betting' },
  { code: '93', label: 'Sports, amusement and recreation' },
  { code: '94', label: 'Activities of membership organisations' },
  { code: '95', label: 'Repair of computers and personal goods' },
  { code: '96', label: 'Other personal service activities' },
  { code: '97', label: 'Activities of households as employers' },
  { code: '98', label: 'Undifferentiated household goods/services production' },
  { code: '99', label: 'Activities of extraterritorial organisations' },
]

const STAGES = ['Prospect', 'Suspect', 'Opportunity'] as const
type Stage = typeof STAGES[number]

const STAGE_COLORS: Record<Stage, string> = {
  Prospect:    'var(--ns-text-muted)',
  Suspect:     'var(--ns-warn)',
  Opportunity: 'var(--ns-brand)',
}
const STAGE_BG: Record<Stage, string> = {
  Prospect:    'var(--ns-surface-2)',
  Suspect:     'oklch(0.97 0.04 80)',
  Opportunity: 'oklch(0.96 0.03 198)',
}

const INTEGRATION_LABELS: Record<string, string> = {
  none:    'Not integrated',
  partial: 'Partial',
  full:    'Full',
}

const FIT_COLORS: Record<number, string> = {
  5: 'oklch(0.35 0.10 160)',
  4: 'oklch(0.35 0.08 165)',
  3: 'oklch(0.40 0.10 75)',
  2: 'oklch(0.40 0.12 40)',
  1: 'oklch(0.40 0.10 15)',
}
const FIT_BG: Record<number, string> = {
  5: 'oklch(0.93 0.05 160)',
  4: 'oklch(0.94 0.04 165)',
  3: 'oklch(0.96 0.06 80)',
  2: 'oklch(0.95 0.07 40)',
  1: 'oklch(0.95 0.05 15)',
}
const SENTIMENT_COLORS: Record<string, string> = {
  positive: 'oklch(0.35 0.10 160)',
  negative: 'oklch(0.40 0.10 15)',
  neutral:  'var(--ns-text-muted)',
  mixed:    'oklch(0.40 0.10 75)',
}

const INTEGRATION_COLORS: Record<string, string> = {
  none:    'var(--ns-text-subtle)',
  partial: 'var(--ns-warn)',
  full:    'var(--ns-ok)',
}

// ─── SniCombobox ─────────────────────────────────────────────────────────────

function SniCombobox({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [query, setQuery]   = useState('')
  const [open, setOpen]     = useState(false)
  const containerRef        = useRef<HTMLDivElement>(null)

  const selected = SNI_CODES.find(c => c.code === value)

  const filtered = useMemo(() => {
    if (!query) return SNI_CODES
    const q = query.toLowerCase()
    return SNI_CODES.filter(c => c.code.startsWith(q) || c.label.toLowerCase().includes(q))
  }, [query])

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  function handleSelect(code: string) {
    onChange(code === value ? '' : code)
    setQuery('')
    setOpen(false)
  }

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <div style={{ display: 'flex', gap: 4 }}>
        <input
          className="input mono"
          style={{ fontSize: 12, flex: 1 }}
          placeholder="Search industry…"
          value={open ? query : (selected ? `${selected.code} – ${selected.label}` : '')}
          onFocus={() => { setOpen(true); setQuery('') }}
          onChange={e => setQuery(e.target.value)}
        />
        {value && (
          <button
            type="button"
            style={{ padding: '0 6px', fontSize: 12, cursor: 'pointer', border: '1px solid var(--ns-border)', borderRadius: 4, background: 'transparent', color: 'var(--ns-text-subtle)' }}
            onClick={() => { onChange(''); setQuery('') }}
          >
            ×
          </button>
        )}
      </div>
      {open && (
        <div style={{
          position: 'absolute', zIndex: 50, top: '100%', left: 0, right: 0,
          background: 'var(--ns-surface)', border: '1px solid var(--ns-border)',
          borderRadius: 6, marginTop: 2, maxHeight: 220, overflowY: 'auto',
          boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
        }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '8px 12px', fontSize: 12, color: 'var(--ns-text-subtle)' }}>No results</div>
          ) : filtered.map(c => (
            <div
              key={c.code}
              onMouseDown={() => handleSelect(c.code)}
              style={{
                padding: '6px 12px', fontSize: 12, cursor: 'pointer', display: 'flex', gap: 8,
                background: c.code === value ? 'var(--ns-surface-2)' : 'transparent',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--ns-surface-2)')}
              onMouseLeave={e => (e.currentTarget.style.background = c.code === value ? 'var(--ns-surface-2)' : 'transparent')}
            >
              <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--ns-text-subtle)', minWidth: 24 }}>{c.code}</span>
              <span>{c.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function allabolagUrl(companyId: string) {
  return `https://www.allabolag.se/${companyId}`
}

// ─── SearchTab ────────────────────────────────────────────────────────────────

function SearchTab() {
  const qc = useQueryClient()

  const [legalGroupText,       setLegalGroupText]       = useState('Privat aktiebolag')
  const [statusCode,           setStatusCode]           = useState('100')
  const [plNetOperatingIncome, setPlNetOperatingIncome] = useState('')
  const [industryCode,         setIndustryCode]         = useState('')
  const [hits,                 setHits]                 = useState('50')
  const [searched,             setSearched]             = useState(false)
  const [selected,             setSelected]             = useState<Set<string>>(new Set())

  const searchQuery = useQuery<RoaringSearchResult>({
    queryKey: ['leads-search', legalGroupText, statusCode, plNetOperatingIncome, industryCode, hits],
    queryFn: () => {
      const qs = new URLSearchParams()
      if (legalGroupText)       qs.set('legalGroupText',       legalGroupText)
      if (statusCode)           qs.set('statusCode',           statusCode)
      if (plNetOperatingIncome) qs.set('plNetOperatingIncome', plNetOperatingIncome)
      if (industryCode)         qs.set('industryCode',         industryCode)
      if (hits)                 qs.set('hits',                 hits)
      return api.get(`/leads/search?${qs.toString()}`)
    },
    enabled: searched,
    staleTime: 60_000,
  })

  const saveMutation = useMutation({
    mutationFn: (companies: RoaringHit[]) =>
      api.post('/leads/prospects/batch', { companies }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['prospects'] })
      setSelected(new Set())
      setSearched(false)
    },
  })

  function handleSearch() {
    setSearched(false)
    setSelected(new Set())
    setTimeout(() => setSearched(true), 0)
  }

  function toggleAll(hits: RoaringHit[]) {
    const saveable = hits.filter(h => !h.alreadySaved).map(h => h.companyId)
    if (saveable.every(id => selected.has(id))) {
      setSelected(new Set())
    } else {
      setSelected(new Set(saveable))
    }
  }

  function toggle(id: string) {
    setSelected(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const resultHits = searchQuery.data?.hits ?? []
  const saveableHits = resultHits.filter(h => !h.alreadySaved)
  const allSelected = saveableHits.length > 0 && saveableHits.every(h => selected.has(h.companyId))

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 16, alignItems: 'start' }}>

      {/* Filter panel */}
      <div className="card" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ns-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Filters
        </div>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <span style={{ fontSize: 12, fontWeight: 500 }}>Legal form</span>
          <select className="input" value={legalGroupText} onChange={e => setLegalGroupText(e.target.value)} style={{ fontSize: 12 }}>
            {LEGAL_GROUP_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <span style={{ fontSize: 12, fontWeight: 500 }}>Status</span>
          <select className="input" value={statusCode} onChange={e => setStatusCode(e.target.value)} style={{ fontSize: 12 }}>
            {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <span style={{ fontSize: 12, fontWeight: 500 }}>Net operating income</span>
          <select className="input" value={plNetOperatingIncome} onChange={e => setPlNetOperatingIncome(e.target.value)} style={{ fontSize: 12 }}>
            {INCOME_RANGE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <span style={{ fontSize: 12, fontWeight: 500 }}>Industry (SNI)</span>
          <SniCombobox value={industryCode} onChange={setIndustryCode} />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <span style={{ fontSize: 12, fontWeight: 500 }}>Max results</span>
          <input
            type="number"
            className="input mono"
            value={hits}
            onChange={e => setHits(e.target.value)}
            min="1" max="200"
            style={{ fontSize: 12 }}
          />
        </label>

        <button
          className="btn btn-brand btn-sm"
          onClick={handleSearch}
          disabled={searchQuery.isFetching}
          style={{ marginTop: 4 }}
        >
          <Search size={13} />
          {searchQuery.isFetching ? 'Searching…' : 'Search'}
        </button>
      </div>

      {/* Results panel */}
      <div>
        {!searched && !searchQuery.isFetching && (
          <div className="card" style={{ padding: 48, textAlign: 'center' }}>
            <Search size={28} style={{ color: 'var(--ns-text-subtle)', margin: '0 auto 12px' }} />
            <div style={{ fontSize: 13, color: 'var(--ns-text-muted)' }}>Set filters and click Search to find prospects.</div>
          </div>
        )}

        {searchQuery.isError && (
          <div className="card" style={{ padding: 24, color: 'var(--ns-err)', fontSize: 13 }}>
            Search failed — {(searchQuery.error as Error)?.message ?? 'check Roaring.io credentials or try again.'}
          </div>
        )}

        {searchQuery.data && (
          <>
            <div className="row" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
              <span style={{ fontSize: 12.5, color: 'var(--ns-text-muted)' }}>
                <span className="mono" style={{ fontWeight: 600, color: 'var(--ns-text)' }}>{searchQuery.data.hitCount}</span> total hits
                {resultHits.length < searchQuery.data.hitCount && ` · showing ${resultHits.length}`}
              </span>
              {selected.size > 0 && (
                <button
                  className="btn btn-brand btn-sm"
                  onClick={() => saveMutation.mutate(resultHits.filter(h => selected.has(h.companyId)))}
                  disabled={saveMutation.isPending}
                >
                  <Save size={13} />
                  {saveMutation.isPending ? 'Saving…' : `Save ${selected.size} prospect${selected.size > 1 ? 's' : ''}`}
                </button>
              )}
            </div>

            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: 36 }}>
                      <button
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', color: 'var(--ns-text-muted)' }}
                        onClick={() => toggleAll(resultHits)}
                        title="Select all"
                      >
                        {allSelected ? <CheckSquare size={15} /> : <Square size={15} />}
                      </button>
                    </th>
                    <th>Company</th>
                    <th style={{ width: 120 }}>Org nr</th>
                    <th style={{ width: 160 }}>Legal form</th>
                    <th style={{ width: 160 }}>Town</th>
                    <th style={{ width: 100 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {resultHits.map(h => {
                    const isSaved    = !!h.alreadySaved
                    const isSelected = selected.has(h.companyId)
                    return (
                      <tr key={h.companyId} style={{ opacity: isSaved ? 0.55 : 1 }}>
                        <td>
                          {!isSaved && (
                            <button
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', color: isSelected ? 'var(--ns-brand)' : 'var(--ns-text-muted)' }}
                              onClick={() => toggle(h.companyId)}
                            >
                              {isSelected ? <CheckSquare size={15} /> : <Square size={15} />}
                            </button>
                          )}
                        </td>
                        <td style={{ fontWeight: 500 }}>{h.companyName}</td>
                        <td className="mono tabular" style={{ fontSize: 12 }}>
                          <a href={allabolagUrl(h.companyId)} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: 'inherit', textDecoration: 'none' }}
                            onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                            onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
                          >
                            {h.companyId} <ExternalLink size={10} />
                          </a>
                        </td>
                        <td style={{ fontSize: 12 }}>{h.legalGroupText || h.legalGroupCode || '—'}</td>
                        <td style={{ fontSize: 12 }}>{h.town || '—'}</td>
                        <td>
                          {isSaved
                            ? <span className="badge badge-ok" style={{ fontSize: 10 }}>Saved</span>
                            : null
                          }
                        </td>
                      </tr>
                    )
                  })}
                  {resultHits.length === 0 && (
                    <tr>
                      <td colSpan={6} className="muted" style={{ textAlign: 'center', padding: 32 }}>No results.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// ─── CompanyStructureEditor ───────────────────────────────────────────────────

const FLAG_OPTIONS = [
  { value: '🇸🇪', label: '🇸🇪 Sweden' },
  { value: '🇳🇴', label: '🇳🇴 Norway' },
  { value: '🇩🇰', label: '🇩🇰 Denmark' },
  { value: '🇫🇮', label: '🇫🇮 Finland' },
  { value: '🇩🇪', label: '🇩🇪 Germany' },
  { value: '🇬🇧', label: '🇬🇧 UK' },
  { value: '🇺🇸', label: '🇺🇸 USA' },
  { value: '🇳🇱', label: '🇳🇱 Netherlands' },
]

function buildTree(nodes: CompanyStructureNode[], parentId: string | null, depth: number): React.ReactNode[] {
  return nodes
    .filter(n => n.parentId === parentId)
    .flatMap(n => [
      <div key={n.id} style={{ display: 'flex', alignItems: 'center', gap: 6, paddingLeft: depth * 20, minHeight: 28 }}>
        {depth > 0 && (
          <span style={{ color: 'var(--ns-border)', fontSize: 10, marginRight: 2 }}>└</span>
        )}
        <span style={{ fontSize: 13 }}>{n.flag}</span>
        {n.percentage != null && (
          <span className="mono" style={{ fontSize: 11, color: 'var(--ns-text-muted)', minWidth: 32 }}>{n.percentage}%</span>
        )}
        <span style={{ fontSize: 12, flex: 1 }}>{n.name}</span>
      </div>,
      ...buildTree(nodes, n.id, depth + 1),
    ])
}

function CompanyStructureEditor({
  nodes,
  onChange,
}: {
  nodes: CompanyStructureNode[]
  onChange: (nodes: CompanyStructureNode[]) => void
}) {
  const [showForm, setShowForm] = useState(false)
  const [name,       setName]       = useState('')
  const [flag,       setFlag]       = useState('🇸🇪')
  const [percentage, setPercentage] = useState('')
  const [parentId,   setParentId]   = useState<string>('')

  function addNode() {
    if (!name.trim()) return
    const node: CompanyStructureNode = {
      id:         `${Date.now()}`,
      name:       name.trim(),
      flag,
      percentage: percentage ? parseFloat(percentage) : null,
      parentId:   parentId || null,
    }
    onChange([...nodes, node])
    setName(''); setPercentage(''); setFlag('🇸🇪'); setParentId(''); setShowForm(false)
  }

  function removeNode(id: string) {
    const idsToRemove = new Set<string>()
    function collect(nid: string) {
      idsToRemove.add(nid)
      nodes.filter(n => n.parentId === nid).forEach(n => collect(n.id))
    }
    collect(id)
    onChange(nodes.filter(n => !idsToRemove.has(n.id)))
  }

  const roots = nodes.filter(n => n.parentId === null)

  return (
    <div>
      {nodes.length === 0 ? (
        <div className="muted" style={{ fontSize: 12, padding: '4px 0 8px' }}>No structure added yet.</div>
      ) : (
        <div style={{ marginBottom: 10, border: '1px solid var(--ns-border)', borderRadius: 6, padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 2 }}>
          {nodes.map(n => {
            const depth = (() => {
              let d = 0, cur: string | null = n.parentId
              while (cur) { d++; cur = nodes.find(x => x.id === cur)?.parentId ?? null }
              return d
            })()
            return (
              <div key={n.id} style={{ display: 'flex', alignItems: 'center', gap: 6, paddingLeft: depth * 18 }}>
                {depth > 0 && <span style={{ color: 'var(--ns-border)', fontSize: 10 }}>└</span>}
                <span style={{ fontSize: 13 }}>{n.flag}</span>
                {n.percentage != null && (
                  <span className="mono" style={{ fontSize: 11, color: 'var(--ns-text-muted)', minWidth: 34 }}>{n.percentage}%</span>
                )}
                <span style={{ fontSize: 12, flex: 1 }}>{n.name}</span>
                <button
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--ns-text-subtle)', lineHeight: 1 }}
                  onClick={() => removeNode(n.id)}
                  title="Remove"
                >
                  <Trash2 size={11} />
                </button>
              </div>
            )
          })}
        </div>
      )}

      {showForm ? (
        <div className="card" style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <input
            className="input"
            placeholder="Company name *"
            value={name}
            onChange={e => setName(e.target.value)}
            style={{ fontSize: 12 }}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <select className="input" value={flag} onChange={e => setFlag(e.target.value)} style={{ fontSize: 12, flex: 1 }}>
              {FLAG_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <input
              className="input mono"
              type="number"
              placeholder="% ownership"
              value={percentage}
              onChange={e => setPercentage(e.target.value)}
              min="0" max="100"
              style={{ fontSize: 12, width: 110 }}
            />
          </div>
          <select
            className="input"
            value={parentId}
            onChange={e => setParentId(e.target.value)}
            style={{ fontSize: 12 }}
          >
            <option value="">No parent (root)</option>
            {nodes.map(n => <option key={n.id} value={n.id}>{n.flag} {n.name}</option>)}
          </select>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-brand btn-sm" disabled={!name.trim()} onClick={addNode}>Add</button>
            <button className="btn btn-ghost btn-sm" onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </div>
      ) : (
        <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }} onClick={() => setShowForm(true)}>
          <Plus size={12} />Add company
        </button>
      )}
    </div>
  )
}

// ─── ProspectResearchTab ──────────────────────────────────────────────────────

function FitBadge({ score }: { score: number }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: 36, height: 36, borderRadius: '50%',
      fontSize: 18, fontWeight: 800, fontFamily: 'var(--font-mono)',
      color: FIT_COLORS[score], background: FIT_BG[score],
      border: `2px solid ${FIT_COLORS[score]}`,
    }}>{score}</span>
  )
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px', color: 'var(--ns-text-subtle)', fontSize: 11 }}
      title="Copy"
      onClick={() => { navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500) }}
    >{copied ? '✓' : '⎘'}</button>
  )
}

function FinancialRow({ fin }: { fin: LeadResearchFinancial }) {
  const fmt = (n: number | null) => n == null ? '—' : `${fin.isApproximate ? '~' : ''}${(n / 1000).toFixed(1)}`
  return (
    <tr>
      <td className="mono tabular" style={{ fontSize: 12 }}>{fin.year}</td>
      <td className="mono tabular" style={{ fontSize: 12, textAlign: 'right' }}>
        {fin.revenueKsek != null
          ? <span title={fin.isApproximate ? 'Approximate' : undefined}>{fmt(fin.revenueKsek)} MSEK</span>
          : <span className="muted">—</span>}
      </td>
      <td className="mono tabular" style={{ fontSize: 12, textAlign: 'right' }}>
        {fin.ebitMarginPct != null
          ? <span>{fin.ebitMarginPct.toFixed(1)}%</span>
          : <span className="muted">—</span>}
      </td>
      {fin.note && <td style={{ fontSize: 11, color: 'var(--ns-text-muted)' }}>{fin.note}</td>}
    </tr>
  )
}

function NewsItem({ item }: { item: LeadResearchNews }) {
  return (
    <div style={{
      padding: '10px 12px',
      borderLeft: `3px solid ${SENTIMENT_COLORS[item.sentiment ?? 'neutral'] ?? 'var(--ns-border)'}`,
      background: 'var(--ns-surface-2)', borderRadius: '0 6px 6px 0', marginBottom: 6,
    }}>
      <div className="row" style={{ justifyContent: 'space-between', gap: 8, marginBottom: 2 }}>
        <span style={{ fontSize: 12.5, fontWeight: 500, flex: 1 }}>
          {item.url
            ? <a href={item.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--ns-text)', textDecoration: 'none' }}>{item.headline}</a>
            : item.headline}
        </span>
        {item.relevance === 'HIGH' && (
          <span style={{ fontSize: 10, fontWeight: 700, color: FIT_COLORS[5], background: FIT_BG[5], padding: '1px 6px', borderRadius: 8, flexShrink: 0 }}>HIGH</span>
        )}
      </div>
      <div className="row" style={{ gap: 8 }}>
        {item.sourceName && <span className="muted" style={{ fontSize: 11 }}>{item.sourceName}</span>}
        {(item.publishedDate || item.publishedLabel) && (
          <span className="muted" style={{ fontSize: 11 }}>
            {item.publishedDate
              ? new Date(item.publishedDate).toLocaleDateString('sv-SE')
              : item.publishedLabel}
          </span>
        )}
      </div>
      {item.summary && <div style={{ fontSize: 11.5, color: 'var(--ns-text-muted)', marginTop: 4 }}>{item.summary}</div>}
    </div>
  )
}

interface ProspectResearchTabProps {
  prospect:        Prospect
  research:        LeadResearch | null
  history:         LeadResearch[]
  historyLoading:  boolean
  isLoading:       boolean
  isError:         boolean
  isPending:       boolean
  onResearch:      (force: boolean) => void
  sourcesOpen:     boolean
  onToggleSources: () => void
}

const RESEARCH_STATUS_LABELS: Record<LeadResearch['status'], { label: string; color: string; bg: string }> = {
  success:           { label: 'Success',     color: 'oklch(0.42 0.13 145)', bg: 'oklch(0.96 0.04 145)' },
  validation_failed: { label: 'Malformed',   color: 'oklch(0.50 0.13 60)',  bg: 'oklch(0.97 0.04 80)' },
  api_error:         { label: 'API error',   color: 'oklch(0.50 0.18 25)',  bg: 'oklch(0.96 0.04 25)' },
  partial:           { label: 'Partial',     color: 'oklch(0.50 0.13 60)',  bg: 'oklch(0.97 0.04 80)' },
}

function ResearchHistoryList({ history }: { history: LeadResearch[] }) {
  if (history.length === 0) return null
  return (
    <section>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ns-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
        Research history
      </div>
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table className="table" style={{ width: '100%', fontSize: 12 }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>When</th>
              <th style={{ textAlign: 'left' }}>By</th>
              <th style={{ textAlign: 'left' }}>Status</th>
              <th style={{ textAlign: 'center', width: 50 }}>Fit</th>
              <th style={{ textAlign: 'right',  width: 90 }}>Cost (USD)</th>
            </tr>
          </thead>
          <tbody>
            {history.map(h => {
              const meta = RESEARCH_STATUS_LABELS[h.status] ?? RESEARCH_STATUS_LABELS.api_error
              return (
                <tr key={h.id}>
                  <td className="mono" style={{ fontSize: 11.5 }}>
                    {new Date(h.createdAt).toLocaleString('sv-SE')}
                  </td>
                  <td style={{ fontSize: 11.5 }}>
                    {h.createdByName ?? <span className="muted">—</span>}
                  </td>
                  <td>
                    <span style={{
                      display: 'inline-block', padding: '1px 7px', borderRadius: 10,
                      fontSize: 10.5, fontWeight: 600,
                      color: meta.color, background: meta.bg,
                    }}>{meta.label}</span>
                  </td>
                  <td style={{ textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: 11.5 }}>
                    {h.clementineFitScore ?? <span className="muted">—</span>}
                  </td>
                  <td className="mono tabular" style={{ textAlign: 'right', fontSize: 11.5 }}>
                    {h.costMetadata?.usdCost != null
                      ? `$${h.costMetadata.usdCost.toFixed(2)}`
                      : <span className="muted">—</span>}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function ProspectResearchTab({
  prospect, research, history, historyLoading, isLoading, isError, isPending, onResearch, sourcesOpen, onToggleSources,
}: ProspectResearchTabProps) {
  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 0' }}>
        <div style={{ fontSize: 13, color: 'var(--ns-text-muted)', marginBottom: 8 }}>Loading research…</div>
      </div>
    )
  }

  if (isError) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--ns-error)', fontSize: 13 }}>
        Failed to load research. Try again.
      </div>
    )
  }

  if (isPending) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 0' }}>
        <Sparkles size={24} style={{ color: 'var(--ns-brand)', marginBottom: 12 }} />
        <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Researching…</div>
        <div style={{ fontSize: 12, color: 'var(--ns-text-muted)' }}>
          This typically takes 30–90 seconds and involves several web searches.
        </div>
      </div>
    )
  }

  if (!research) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <div style={{ textAlign: 'center', padding: '48px 24px' }}>
          <Sparkles size={28} style={{ color: 'var(--ns-brand)', marginBottom: 16 }} />
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>No research yet</div>
          <div style={{ fontSize: 12.5, color: 'var(--ns-text-muted)', marginBottom: 20, maxWidth: 280, margin: '0 auto 20px' }}>
            Click below to run AI-powered research on {prospect.companyName}.
            This uses web search and takes about 1 minute.
          </div>
          <button
            className="btn btn-brand btn-sm"
            onClick={() => onResearch(false)}
            disabled={isPending}
          >
            <Sparkles size={13} />Research this company
          </button>
        </div>
        <ResearchHistoryList history={history} />
      </div>
    )
  }

  const sectionLabel = (text: string) => (
    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ns-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
      {text}
    </div>
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Header strip */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14 }}>{research.legalName ?? prospect.companyName}</div>
            {research.tradingName && research.tradingName !== research.legalName && (
              <div style={{ fontSize: 12, color: 'var(--ns-text-muted)' }}>"{research.tradingName}"</div>
            )}
            <div className="row" style={{ gap: 10, marginTop: 4, flexWrap: 'wrap' }}>
              {research.orgNumber && <span className="mono muted" style={{ fontSize: 11 }}>{research.orgNumber}</span>}
              {research.website && (
                <a href={research.website} target="_blank" rel="noopener noreferrer"
                  className="row" style={{ gap: 3, fontSize: 11.5, color: 'var(--ns-brand)', textDecoration: 'none' }}>
                  {research.website.replace(/^https?:\/\//, '')} <ExternalLink size={9} />
                </a>
              )}
              {research.switchboardPhone && (
                <span className="muted" style={{ fontSize: 11.5 }}>{research.switchboardPhone}</span>
              )}
            </div>
          </div>
          <div className="row" style={{ gap: 8, flexShrink: 0 }}>
            <span style={{ fontSize: 11, color: 'var(--ns-text-subtle)' }}>
              {new Date(research.createdAt).toLocaleDateString('sv-SE')}
            </span>
            <button
              className="btn btn-ghost btn-sm"
              style={{ fontSize: 11 }}
              onClick={() => onResearch(true)}
              disabled={isPending}
              title="Re-run research"
            >
              <Sparkles size={12} />Re-run
            </button>
          </div>
        </div>
        {research.status !== 'success' && (
          <div style={{ fontSize: 11.5, color: 'var(--ns-warn)', background: 'oklch(0.97 0.04 80)', padding: '6px 10px', borderRadius: 6 }}>
            Research status: <strong>{research.status}</strong>
            {research.status === 'validation_failed' && ' — the model response was malformed. Re-run or contact admin.'}
          </div>
        )}
      </div>

      {/* Approach card */}
      {research.clementineFitScore != null && (
        <section>
          {sectionLabel('Clementine Fit')}
          <div className="card" style={{ padding: 16 }}>
            <div className="row" style={{ gap: 16, alignItems: 'flex-start' }}>
              <FitBadge score={research.clementineFitScore} />
              <div style={{ flex: 1 }}>
                {research.clementineFitReasoning && (
                  <div style={{ fontSize: 12.5, marginBottom: 10 }}>{research.clementineFitReasoning}</div>
                )}
                {research.recommendedApproach && (
                  <div style={{ fontSize: 12, color: 'var(--ns-text-muted)', borderTop: '1px solid var(--ns-border)', paddingTop: 10 }}>
                    <strong style={{ color: 'var(--ns-text)', fontWeight: 600 }}>Approach: </strong>
                    {research.recommendedApproach}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Key contacts */}
      {(research.ceoName || research.cfoName || research.contacts.length > 0) && (
        <section>
          {sectionLabel('Key Contacts')}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {research.ceoName && (
              <div className="card" style={{ padding: 12 }}>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{research.ceoName}</div>
                    <div className="muted" style={{ fontSize: 11.5 }}>CEO / VD</div>
                  </div>
                  <div className="row" style={{ gap: 2 }}>
                    {research.ceoEmail && <CopyButton value={research.ceoEmail} />}
                    {research.ceoPhone && <CopyButton value={research.ceoPhone} />}
                  </div>
                </div>
                <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {research.ceoEmail && <a href={`mailto:${research.ceoEmail}`} style={{ fontSize: 11.5, color: 'var(--ns-brand)', textDecoration: 'none' }}>{research.ceoEmail}</a>}
                  {research.ceoPhone && <span className="muted" style={{ fontSize: 11.5 }}>{research.ceoPhone}</span>}
                  {research.ceoNote && <span className="muted" style={{ fontSize: 11 }}>{research.ceoNote}</span>}
                </div>
              </div>
            )}
            {/* TODO: CFO enrichment via LinkedIn — separate ticket */}
            {research.cfoName && (
              <div className="card" style={{ padding: 12 }}>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{research.cfoName}</div>
                    <div className="muted" style={{ fontSize: 11.5 }}>CFO / Ekonomichef</div>
                  </div>
                  <div className="row" style={{ gap: 2 }}>
                    {research.cfoEmail && <CopyButton value={research.cfoEmail} />}
                    {research.cfoPhone && <CopyButton value={research.cfoPhone} />}
                  </div>
                </div>
                <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {research.cfoEmail && <a href={`mailto:${research.cfoEmail}`} style={{ fontSize: 11.5, color: 'var(--ns-brand)', textDecoration: 'none' }}>{research.cfoEmail}</a>}
                  {research.cfoPhone && <span className="muted" style={{ fontSize: 11.5 }}>{research.cfoPhone}</span>}
                  {research.cfoNote && <span className="muted" style={{ fontSize: 11 }}>{research.cfoNote}</span>}
                </div>
              </div>
            )}
            {research.contacts.map(c => (
              <div key={c.id} className="card" style={{ padding: 12 }}>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{c.name}</div>
                    {c.title && <div className="muted" style={{ fontSize: 11.5 }}>{c.title}</div>}
                  </div>
                  <div className="row" style={{ gap: 2 }}>
                    {c.email && <CopyButton value={c.email} />}
                    {c.phone && <CopyButton value={c.phone} />}
                  </div>
                </div>
                <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {c.email && <a href={`mailto:${c.email}`} style={{ fontSize: 11.5, color: 'var(--ns-brand)', textDecoration: 'none' }}>{c.email}</a>}
                  {c.phone && <span className="muted" style={{ fontSize: 11.5 }}>{c.phone}</span>}
                  {c.linkedinUrl && (
                    <a href={c.linkedinUrl} target="_blank" rel="noopener noreferrer" className="row"
                      style={{ gap: 3, fontSize: 11.5, color: 'var(--ns-brand)', textDecoration: 'none', width: 'fit-content' }}>
                      LinkedIn <ExternalLink size={9} />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Financials */}
      {research.financials.length > 0 && (
        <section>
          {sectionLabel('Financials')}
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Year</th>
                  <th style={{ textAlign: 'right' }}>Revenue</th>
                  <th style={{ textAlign: 'right' }}>EBIT %</th>
                </tr>
              </thead>
              <tbody>
                {research.financials.map(f => <FinancialRow key={f.id} fin={f} />)}
              </tbody>
            </table>
          </div>
          {research.financials.some(f => f.isApproximate) && (
            <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>~ = approximate value</div>
          )}
        </section>
      )}

      {/* ERP signals */}
      {research.erpSignals.length > 0 && (
        <section>
          {sectionLabel('ERP / Tech Signals')}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {research.erpSignals.map((s, i) => (
              <span key={i} style={{ fontSize: 12, padding: '3px 10px', borderRadius: 12, border: '1px solid var(--ns-border)', background: 'var(--ns-surface-2)' }}>{s}</span>
            ))}
          </div>
        </section>
      )}

      {/* News */}
      {research.news.length > 0 && (
        <section>
          {sectionLabel('Recent News')}
          {research.news.map(item => <NewsItem key={item.id} item={item} />)}
        </section>
      )}

      {/* Group structure */}
      {(research.ultimateParent || research.subsidiariesCount != null || research.relatedEntities.length > 0) && (
        <section>
          {sectionLabel('Group Structure')}
          <div className="card" style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {research.ultimateParent && (
              <div style={{ fontSize: 12.5 }}>
                <span className="muted">Ultimate parent: </span>{research.ultimateParent}
              </div>
            )}
            {research.intermediateParent && (
              <div style={{ fontSize: 12.5 }}>
                <span className="muted">Intermediate parent: </span>{research.intermediateParent}
              </div>
            )}
            {research.groupRole && (
              <div style={{ fontSize: 12.5 }}>
                <span className="muted">Role: </span>{research.groupRole}
              </div>
            )}
            {research.subsidiariesCount != null && (
              <div style={{ fontSize: 12.5 }}>
                <span className="muted">Subsidiaries: </span>{research.subsidiariesCount}
                {research.groupSizeCompanies != null && ` (${research.groupSizeCompanies} total in group)`}
              </div>
            )}
            {research.relatedEntities.length > 0 && (
              <div style={{ marginTop: 6 }}>
                {research.relatedEntities.map((e, i) => (
                  <div key={i} style={{ fontSize: 12, padding: '4px 0', borderTop: '1px solid var(--ns-border)', display: 'flex', gap: 8 }}>
                    {e.orgNumber && <span className="mono muted" style={{ fontSize: 11 }}>{e.orgNumber}</span>}
                    <span style={{ fontWeight: 500 }}>{e.name}</span>
                    <span className="muted">{e.role}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Sources & confidence */}
      <section>
        <button
          style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--ns-text-muted)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}
          onClick={onToggleSources}
        >
          {sourcesOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          Sources & Confidence
        </button>
        {sourcesOpen && (
          <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {research.confidenceNotes && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {Object.entries(research.confidenceNotes).map(([key, val]) => (
                  <span key={key} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, border: '1px solid var(--ns-border)', background: 'var(--ns-surface-2)' }}>
                    {key}: <strong>{val}</strong>
                  </span>
                ))}
              </div>
            )}
            {research.sources.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {research.sources.map((url, i) => (
                  <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                    style={{ fontSize: 11, color: 'var(--ns-brand)', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {url}
                  </a>
                ))}
              </div>
            )}
          </div>
        )}
      </section>

      {historyLoading
        ? <div style={{ fontSize: 11.5, color: 'var(--ns-text-muted)' }}>Loading history…</div>
        : <ResearchHistoryList history={history} />}

    </div>
  )
}

// ─── ProspectDetailDrawer ─────────────────────────────────────────────────────

function ProspectDetailDrawer({ prospect, onClose }: { prospect: Prospect; onClose: () => void }) {
  const qc = useQueryClient()

  const { data: detail } = useQuery<Prospect>({
    queryKey: ['prospect', prospect.id],
    queryFn:  () => api.get(`/leads/prospects/${prospect.id}`),
  })

  const { data: sourceSystems = [] } = useQuery<SourceSystem[]>({
    queryKey: ['source-systems'],
    queryFn:  () => api.get('/leads/source-systems'),
  })

  const p = detail ?? prospect

  const [phase,              setPhase]              = useState(p.phase)
  const [companiesInScope,   setCompaniesInScope]   = useState<string>(String(p.companiesInScope ?? ''))
  const [hasBudgetForecast,  setHasBudgetForecast]  = useState(p.hasBudgetForecast)
  const [notes,              setNotes]              = useState(p.notes ?? '')
  const [companyStructure,   setCompanyStructure]   = useState<CompanyStructureNode[]>(p.companyStructure ?? [])
  const [arrValue,           setArrValue]           = useState<string>(String(p.arrValue ?? ''))

  // New contact form
  const [contactName,     setContactName]     = useState('')
  const [contactRole,     setContactRole]     = useState('')
  const [contactEmail,    setContactEmail]    = useState('')
  const [contactPhone,    setContactPhone]    = useState('')
  const [contactLinkedin, setContactLinkedin] = useState('')
  const [showContactForm, setShowContactForm] = useState(false)

  const [selectedSystemId, setSelectedSystemId] = useState('')
  const [drawerTab,        setDrawerTab]        = useState<'details' | 'research'>('details')
  const [sourcesOpen,      setSourcesOpen]      = useState(false)

  const {
    data: research,
    isLoading: researchLoading,
    isError: researchError,
  } = useQuery<LeadResearch | null>({
    queryKey: ['prospect-research', p.id],
    queryFn: async () => {
      try {
        return await api.get<LeadResearch>(`/leads/prospects/${p.id}/research/latest`)
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null
        throw e
      }
    },
    enabled: drawerTab === 'research',
  })

  const researchToastId = useRef<string | number | null>(null)
  const researchMutation = useMutation({
    mutationFn: (force: boolean) =>
      api.post<{ research: LeadResearch }>(`/leads/prospects/${p.id}/research`, { force }),
    onMutate: () => {
      researchToastId.current = toast.loading(
        `Researching ${p.companyName}…`,
        { description: 'This typically takes 30–90 seconds — web searches in progress.', duration: Infinity },
      )
    },
    onSuccess: () => {
      if (researchToastId.current != null) {
        toast.success(`Research complete for ${p.companyName}`, { id: researchToastId.current })
        researchToastId.current = null
      }
      qc.invalidateQueries({ queryKey: ['prospect-research',         p.id] })
      qc.invalidateQueries({ queryKey: ['prospect-research-history', p.id] })
      qc.invalidateQueries({ queryKey: ['prospects'] })
    },
    onError: (e) => {
      const status  = e instanceof ApiError ? e.status  : 0
      const message = e instanceof ApiError ? e.message : (e as Error)?.message ?? 'Unknown error'
      const title =
        status === 429 ? 'Rate limited'
      : status === 422 ? 'Model response invalid'
      : status === 503 ? 'Service unavailable'
      : status === 504 ? 'Request timed out'
      : status >= 400  ? `Research failed (${status})`
      :                  'Research failed'
      if (researchToastId.current != null) {
        toast.error(title, { id: researchToastId.current, description: message, duration: 8000 })
        researchToastId.current = null
      } else {
        toast.error(title, { description: message, duration: 8000 })
      }
      // 409 (recent research exists) and 422 still produce useful state — refresh the cache
      qc.invalidateQueries({ queryKey: ['prospect-research',         p.id] })
      qc.invalidateQueries({ queryKey: ['prospect-research-history', p.id] })
      qc.invalidateQueries({ queryKey: ['prospects'] })
    },
  })

  const {
    data: researchHistory,
    isLoading: historyLoading,
  } = useQuery<LeadResearch[]>({
    queryKey: ['prospect-research-history', p.id],
    queryFn:  () => api.get<LeadResearch[]>(`/leads/prospects/${p.id}/research`),
    enabled:  drawerTab === 'research',
  })

  const updateMutation = useMutation({
    mutationFn: () => api.put(`/leads/prospects/${p.id}`, {
      phase,
      companiesInScope:  companiesInScope ? parseInt(companiesInScope) : null,
      hasBudgetForecast,
      notes:             notes.trim() || null,
      companyStructure:  companyStructure.length > 0 ? companyStructure : null,
      arrValue:          arrValue ? parseInt(arrValue) : null,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['prospects'] }),
  })

  const fetchStructureMutation = useMutation({
    mutationFn: () => api.get<CompanyStructureNode[]>(`/leads/prospects/${p.id}/group-structure`),
    onSuccess: (nodes) => setCompanyStructure(nodes),
  })

  const addContactMutation = useMutation({
    mutationFn: (body: object) => api.post<ProspectContact>(`/leads/prospects/${p.id}/contacts`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['prospect', p.id] })
      setContactName(''); setContactRole(''); setContactEmail('')
      setContactPhone(''); setContactLinkedin(''); setShowContactForm(false)
    },
  })

  const deleteContactMutation = useMutation({
    mutationFn: (contactId: number) => api.delete(`/leads/prospects/${p.id}/contacts/${contactId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['prospect', p.id] }),
  })

  const addSystemMutation = useMutation({
    mutationFn: (sourceSystemId: number) =>
      api.post(`/leads/prospects/${p.id}/source-systems`, { sourceSystemId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['prospect', p.id] })
      setSelectedSystemId('')
    },
  })

  const removeSystemMutation = useMutation({
    mutationFn: (systemId: number) => api.delete(`/leads/prospects/${p.id}/source-systems/${systemId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['prospect', p.id] }),
  })

  const assignedSystemIds = new Set((p.sourceSystems ?? []).map(s => s.id))
  const availableSystems  = sourceSystems.filter(s => !assignedSystemIds.has(s.id))

  return (
    <Drawer open onClose={onClose}>
      <div style={{ padding: 20, display: 'flex', flexDirection: 'column', height: '100%' }}>

        {/* Header */}
        <div className="row" style={{ justifyContent: 'space-between', marginBottom: 4 }}>
          <div style={{ minWidth: 0 }}>
            <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{p.companyName}</h2>
            <div className="row" style={{ gap: 8, marginTop: 4 }}>
              <span className="mono muted" style={{ fontSize: 11.5 }}>{p.companyId}</span>
              <a
                href={allabolagUrl(p.companyId)}
                target="_blank"
                rel="noopener noreferrer"
                className="row"
                style={{ gap: 3, fontSize: 11.5, color: 'var(--ns-brand)', textDecoration: 'none' }}
              >
                allabolag.se <ExternalLink size={10} />
              </a>
            </div>
          </div>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}><X size={15} /></button>
        </div>

        <div style={{ fontSize: 11.5, color: 'var(--ns-text-muted)', marginBottom: 12 }}>
          {[p.legalGroupText, p.town].filter(Boolean).join(' · ')}
          {' · '}
          <span style={{ color: 'var(--ns-text-subtle)' }}>fetched {new Date(p.roaringFetchedAt).toLocaleDateString('sv-SE')}</span>
        </div>

        {/* Drawer tab bar */}
        <div className="row" style={{ gap: 4, padding: 3, background: 'var(--ns-surface-2)', border: '1px solid var(--ns-border)', borderRadius: 8, width: 'fit-content', marginBottom: 16 }}>
          {([['details', 'Details'], ['research', 'Research']] as const).map(([id, label]) => (
            <button key={id} onClick={() => setDrawerTab(id)} style={{
              padding: '4px 12px', borderRadius: 5, border: 'none',
              background: drawerTab === id ? 'var(--ns-surface)' : 'transparent',
              boxShadow:  drawerTab === id ? 'var(--ns-shadow-sm)' : 'none',
              fontSize: 12, fontWeight: 500, cursor: 'pointer',
              color: drawerTab === id ? 'var(--ns-text)' : 'var(--ns-text-muted)',
            }}>{label}</button>
          ))}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 20 }}>

          {drawerTab === 'details' && (<>

          {/* Basics */}
          <section>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ns-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>
              Basics
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <span style={{ fontSize: 12, fontWeight: 500 }}>Stage</span>
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <div style={{ display: 'flex', gap: 6 }}>
                  {(['Prospect', 'Suspect', 'Opportunity'] as const).map(s => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setPhase(s)}
                      style={{
                        flex: 1, padding: '5px 0', fontSize: 11.5, fontWeight: 500,
                        border: '1px solid',
                        borderRadius: 6, cursor: 'pointer',
                        borderColor:      phase === s ? STAGE_COLORS[s] : 'var(--ns-border)',
                        background:       phase === s ? STAGE_BG[s]     : 'transparent',
                        color:            phase === s ? STAGE_COLORS[s] : 'var(--ns-text-muted)',
                      }}
                    >{s}</button>
                  ))}
                </div>
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <span style={{ fontSize: 12, fontWeight: 500 }}>Companies in scope</span>
                <input
                  type="number"
                  className="input mono"
                  value={companiesInScope}
                  onChange={e => setCompaniesInScope(e.target.value)}
                  placeholder="e.g. 5"
                  min="1"
                  style={{ fontSize: 13 }}
                />
                <span className="muted" style={{ fontSize: 11 }}>Number of companies that would use Clementine.</span>
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <span style={{ fontSize: 12, fontWeight: 500 }}>ARR Value (SEK)</span>
                <input
                  type="number"
                  className="input mono"
                  value={arrValue}
                  onChange={e => setArrValue(e.target.value)}
                  placeholder="e.g. 120000"
                  min="0"
                  style={{ fontSize: 13 }}
                />
              </label>

              <label className="row" style={{ gap: 10, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={hasBudgetForecast}
                  onChange={e => setHasBudgetForecast(e.target.checked)}
                />
                <span style={{ fontSize: 12, fontWeight: 500 }}>Uses Budget / Forecast</span>
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <span style={{ fontSize: 12, fontWeight: 500 }}>Notes</span>
                <textarea
                  className="input"
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Background, context, next steps…"
                  style={{ fontSize: 13, resize: 'vertical' }}
                />
              </label>
            </div>
          </section>

          {/* Contacts */}
          <section>
            <div className="row" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ns-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                <Users size={11} style={{ verticalAlign: 'middle', marginRight: 5 }} />Contacts
              </div>
              <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }} onClick={() => setShowContactForm(v => !v)}>
                <Plus size={12} />{showContactForm ? 'Cancel' : 'Add contact'}
              </button>
            </div>

            {showContactForm && (
              <div className="card" style={{ padding: 14, marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <input className="input" placeholder="Name *" value={contactName} onChange={e => setContactName(e.target.value)} style={{ fontSize: 12 }} />
                <input className="input" placeholder="Role" value={contactRole} onChange={e => setContactRole(e.target.value)} style={{ fontSize: 12 }} />
                <input className="input" placeholder="Email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} style={{ fontSize: 12 }} />
                <input className="input" placeholder="Phone" value={contactPhone} onChange={e => setContactPhone(e.target.value)} style={{ fontSize: 12 }} />
                <input className="input" placeholder="LinkedIn URL" value={contactLinkedin} onChange={e => setContactLinkedin(e.target.value)} style={{ fontSize: 12 }} />
                <button
                  className="btn btn-brand btn-sm"
                  disabled={!contactName.trim() || addContactMutation.isPending}
                  onClick={() => addContactMutation.mutate({
                    name: contactName.trim(), role: contactRole.trim() || null,
                    email: contactEmail.trim() || null, phone: contactPhone.trim() || null,
                    linkedinUrl: contactLinkedin.trim() || null,
                  })}
                >
                  {addContactMutation.isPending ? 'Adding…' : 'Add'}
                </button>
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(p.contacts ?? []).map(c => (
                <div key={c.id} className="card" style={{ padding: 12 }}>
                  <div className="row" style={{ justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{c.name}</div>
                      {c.role && <div className="muted" style={{ fontSize: 11.5 }}>{c.role}</div>}
                    </div>
                    <button
                      className="btn btn-ghost btn-icon btn-sm"
                      onClick={() => deleteContactMutation.mutate(c.id)}
                      title="Remove"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                  <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {c.email && <a href={`mailto:${c.email}`} style={{ fontSize: 11.5, color: 'var(--ns-brand)', textDecoration: 'none' }}>{c.email}</a>}
                    {c.phone && <span className="muted" style={{ fontSize: 11.5 }}>{c.phone}</span>}
                    {c.linkedinUrl && (
                      <a href={c.linkedinUrl} target="_blank" rel="noopener noreferrer" className="row" style={{ gap: 4, fontSize: 11.5, color: 'var(--ns-brand)', textDecoration: 'none', width: 'fit-content' }}>
                        LinkedIn <ExternalLink size={9} />
                      </a>
                    )}
                  </div>
                </div>
              ))}
              {(p.contacts ?? []).length === 0 && !showContactForm && (
                <div className="muted" style={{ fontSize: 12, padding: '8px 0' }}>No contacts yet.</div>
              )}
            </div>
          </section>

          {/* Source systems */}
          <section>
            <div className="row" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ns-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                <Cpu size={11} style={{ verticalAlign: 'middle', marginRight: 5 }} />Source Systems
              </div>
            </div>

            <div className="row" style={{ gap: 8, marginBottom: 10 }}>
              <select
                className="input"
                value={selectedSystemId}
                onChange={e => setSelectedSystemId(e.target.value)}
                style={{ fontSize: 12, flex: 1 }}
              >
                <option value="">Add a system…</option>
                {availableSystems.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
              <button
                className="btn btn-ghost btn-sm"
                disabled={!selectedSystemId || addSystemMutation.isPending}
                onClick={() => addSystemMutation.mutate(parseInt(selectedSystemId))}
              >
                <Plus size={13} />Add
              </button>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {(p.sourceSystems ?? []).map(s => (
                <div key={s.id} className="row" style={{
                  gap: 6, padding: '4px 10px',
                  border: '1px solid var(--ns-border)', borderRadius: 6,
                  fontSize: 12, background: 'var(--ns-surface-2)',
                }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: INTEGRATION_COLORS[s.integrationStatus], flexShrink: 0 }} />
                  <span style={{ fontWeight: 500 }}>{s.name}</span>
                  <span className="muted" style={{ fontSize: 10.5 }}>{s.isCloud ? '☁' : '🖥'} {INTEGRATION_LABELS[s.integrationStatus]}</span>
                  <button
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--ns-text-subtle)', lineHeight: 1, marginLeft: 2 }}
                    onClick={() => removeSystemMutation.mutate(s.id)}
                    title="Remove"
                  >
                    <X size={11} />
                  </button>
                </div>
              ))}
              {(p.sourceSystems ?? []).length === 0 && (
                <div className="muted" style={{ fontSize: 12 }}>No source systems added.</div>
              )}
            </div>
          </section>
          {/* Company structure */}
          <section>
            <div className="row" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ns-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                <Building2 size={11} style={{ verticalAlign: 'middle', marginRight: 5 }} />Company Structure
              </div>
              <button
                className="btn btn-ghost btn-sm"
                style={{ fontSize: 11 }}
                disabled={fetchStructureMutation.isPending}
                onClick={() => fetchStructureMutation.mutate()}
                title="Fetch ownership tree from Roaring.io"
              >
                {fetchStructureMutation.isPending ? 'Fetching…' : '↓ Fetch from Roaring'}
              </button>
            </div>
            {fetchStructureMutation.isError && (
              <div style={{ fontSize: 11, color: 'var(--ns-error)', marginBottom: 8 }}>
                Failed to fetch from Roaring.io — try again.
              </div>
            )}
            <CompanyStructureEditor nodes={companyStructure} onChange={setCompanyStructure} />
          </section>

          </>)}

          {drawerTab === 'research' && (
            <ProspectResearchTab
              prospect={p}
              research={research ?? null}
              history={researchHistory ?? []}
              historyLoading={historyLoading}
              isLoading={researchLoading}
              isError={researchError}
              isPending={researchMutation.isPending}
              onResearch={(force) => researchMutation.mutate(force)}
              sourcesOpen={sourcesOpen}
              onToggleSources={() => setSourcesOpen(v => !v)}
            />
          )}

        </div>

        {drawerTab === 'details' && (
        <div className="row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--ns-border)' }}>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-brand btn-sm"
            onClick={() => updateMutation.mutate()}
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
        )}
      </div>
    </Drawer>
  )
}

// ─── AddProspectDialog ────────────────────────────────────────────────────────

const LEGAL_FORM_CODES: Record<string, string> = {
  'Privat aktiebolag':  'AB',
  'Publikt aktiebolag': 'PUBL',
  'Handelsbolag':       'HB',
  'Kommanditbolag':     'KB',
  'Enskild firma':      'EF',
}

function AddProspectDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient()

  const [companyName,    setCompanyName]    = useState('')
  const [companyId,      setCompanyId]      = useState('')
  const [legalGroupText, setLegalGroupText] = useState('')
  const [town,           setTown]           = useState('')

  const mutation = useMutation({
    mutationFn: () => api.post('/leads/prospects/batch', {
      companies: [{
        companyId:      companyId.trim(),
        companyName:    companyName.trim(),
        legalGroupCode: legalGroupText ? (LEGAL_FORM_CODES[legalGroupText] ?? null) : null,
        legalGroupText: legalGroupText || null,
        town:           town.trim() || null,
      }],
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['prospects'] })
      setCompanyName(''); setCompanyId(''); setLegalGroupText(''); setTown('')
      onClose()
    },
  })

  const canSubmit = companyName.trim() && companyId.trim()

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose() }}>
      <DialogContent style={{ padding: 24 }}>
        <DialogHeader style={{ marginBottom: 8 }}>
          <DialogTitle>Add prospect manually</DialogTitle>
        </DialogHeader>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={{ fontSize: 12, fontWeight: 500 }}>Company name <span style={{ color: 'var(--ns-error)' }}>*</span></span>
            <input
              className="input"
              value={companyName}
              onChange={e => setCompanyName(e.target.value)}
              placeholder="Acme AB"
              style={{ fontSize: 13 }}
              autoFocus
            />
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={{ fontSize: 12, fontWeight: 500 }}>Org nr <span style={{ color: 'var(--ns-error)' }}>*</span></span>
            <input
              className="input mono"
              value={companyId}
              onChange={e => setCompanyId(e.target.value)}
              placeholder="5560000000"
              style={{ fontSize: 13 }}
            />
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={{ fontSize: 12, fontWeight: 500 }}>Legal form</span>
            <select
              className="input"
              value={legalGroupText}
              onChange={e => setLegalGroupText(e.target.value)}
              style={{ fontSize: 13 }}
            >
              <option value="">— Select —</option>
              {LEGAL_GROUP_OPTIONS.filter(o => o.value).map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={{ fontSize: 12, fontWeight: 500 }}>Town</span>
            <input
              className="input"
              value={town}
              onChange={e => setTown(e.target.value)}
              placeholder="Stockholm"
              style={{ fontSize: 13 }}
            />
          </label>

          {mutation.isError && (
            <div style={{ fontSize: 12, color: 'var(--ns-error)' }}>Failed to save — please try again.</div>
          )}
        </div>

        <DialogFooter style={{ marginTop: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-brand btn-sm"
            disabled={!canSubmit || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? 'Saving…' : 'Add prospect'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ─── PipelineTab ──────────────────────────────────────────────────────────────

function fmtArr(v: number | null) {
  if (v == null) return '—'
  return new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK', maximumFractionDigits: 0 }).format(v)
}

function PipelineTab() {
  const [search,       setSearch]       = useState('')
  const [openProspect, setOpenProspect] = useState<Prospect | null>(null)
  const [addOpen,      setAddOpen]      = useState(false)

  const { data: prospects = [], isLoading, isError } = useQuery<Prospect[]>({
    queryKey: ['prospects'],
    queryFn:  () => api.get('/leads/prospects'),
  })

  const filtered = useMemo(() => {
    if (!search.trim()) return prospects
    const q = search.toLowerCase()
    return prospects.filter(p =>
      p.companyName.toLowerCase().includes(q) ||
      p.companyId.includes(q) ||
      (p.town ?? '').toLowerCase().includes(q),
    )
  }, [prospects, search])

  const stageSummary = useMemo(() =>
    STAGES.map(stage => ({
      stage,
      count: prospects.filter(p => p.phase === stage).length,
      arr:   prospects.filter(p => p.phase === stage).reduce((s, p) => s + (p.arrValue ?? 0), 0),
    }))
  , [prospects])

  const grouped = useMemo(() =>
    STAGES.map(stage => ({
      stage,
      items: filtered.filter(p => p.phase === stage),
    })).filter(g => g.items.length > 0)
  , [filtered])

  return (
    <>
      {/* Stage summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
        {stageSummary.map(({ stage, count, arr }) => (
          <div key={stage} className="card" style={{ padding: '14px 18px', borderLeft: `3px solid ${STAGE_COLORS[stage]}` }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: STAGE_COLORS[stage], textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>{stage}</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
              <span style={{ fontSize: 22, fontWeight: 700, fontFamily: 'var(--font-mono)' }}>{count}</span>
              <span style={{ fontSize: 12, color: 'var(--ns-text-muted)' }}>
                {arr > 0 ? fmtArr(arr) + ' ARR' : 'no ARR'}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="row" style={{ gap: 10, marginBottom: 14 }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 320 }}>
          <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--ns-text-muted)', pointerEvents: 'none' }} />
          <input
            className="input"
            placeholder="Filter by name, org nr, town…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ paddingLeft: 32, fontSize: 13 }}
          />
        </div>
        <span className="mono muted" style={{ fontSize: 12, alignSelf: 'center' }}>
          {filtered.length} lead{filtered.length !== 1 ? 's' : ''}
        </span>
        <button className="btn btn-brand btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setAddOpen(true)}>
          <Plus size={13} />Add prospect
        </button>
      </div>

      <AddProspectDialog open={addOpen} onClose={() => setAddOpen(false)} />

      {isLoading && <div className="muted" style={{ textAlign: 'center', paddingTop: 48, fontSize: 13 }}>Loading…</div>}
      {isError   && <div className="muted" style={{ textAlign: 'center', paddingTop: 48, fontSize: 13, color: 'var(--ns-error)' }}>Failed to load pipeline.</div>}

      {!isLoading && !isError && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {grouped.length === 0 && (
            <div className="muted" style={{ textAlign: 'center', paddingTop: 48, fontSize: 13 }}>
              No leads yet. Use Prospect Generation to find and save prospects.
            </div>
          )}
          {grouped.map(({ stage, items }) => (
            <div key={stage}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                marginBottom: 8, paddingBottom: 6,
                borderBottom: `2px solid ${STAGE_COLORS[stage]}`,
              }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: STAGE_COLORS[stage], textTransform: 'uppercase', letterSpacing: '0.06em' }}>{stage}</span>
                <span className="mono muted" style={{ fontSize: 11 }}>{items.length}</span>
              </div>
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Company</th>
                      <th style={{ width: 130 }}>Org nr</th>
                      <th style={{ width: 130 }}>Town</th>
                      <th style={{ width: 80, textAlign: 'center' }}>Contacts</th>
                      <th style={{ width: 80, textAlign: 'right' }}>ARR (SEK)</th>
                      <th style={{ width: 50, textAlign: 'center' }}>Fit</th>
                      <th style={{ width: 80 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(p => (
                      <tr key={p.id} className="row-clickable" onClick={() => setOpenProspect(p)}>
                        <td>
                          <div className="row" style={{ gap: 5, alignItems: 'center' }}>
                            <span style={{ fontWeight: 500 }}>{p.companyName}</span>
                            {p.lastResearchedAt && (
                              <span
                                title={`AI-researched ${new Date(p.lastResearchedAt).toLocaleString('sv-SE')}`}
                                style={{ display: 'inline-flex', color: 'var(--ns-brand)' }}
                              >
                                <Sparkles size={11} />
                              </span>
                            )}
                          </div>
                          {p.hasBudgetForecast && (
                            <span style={{ fontSize: 10, color: 'var(--ns-brand)', fontWeight: 500 }}>Budget / Forecast</span>
                          )}
                        </td>
                        <td>
                          <a
                            href={allabolagUrl(p.companyId)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="row mono"
                            style={{ gap: 4, fontSize: 12, color: 'var(--ns-brand)', textDecoration: 'none' }}
                            onClick={e => e.stopPropagation()}
                          >
                            {p.companyId} <ExternalLink size={10} />
                          </a>
                        </td>
                        <td style={{ fontSize: 12 }}>{p.town || '—'}</td>
                        <td className="mono tabular" style={{ textAlign: 'center', fontSize: 13 }}>
                          {p.contactCount > 0
                            ? <span className="row" style={{ gap: 4, justifyContent: 'center' }}><Users size={12} />{p.contactCount}</span>
                            : <span className="muted">—</span>
                          }
                        </td>
                        <td className="mono tabular" style={{ textAlign: 'right', fontSize: 12 }}>
                          {p.arrValue != null ? p.arrValue.toLocaleString('sv-SE') : <span className="muted">—</span>}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {p.clementineFitScore != null ? (
                            <span style={{
                              display: 'inline-block',
                              padding: '1px 7px', borderRadius: 10,
                              fontSize: 11, fontWeight: 700, fontFamily: 'var(--font-mono)',
                              color: FIT_COLORS[p.clementineFitScore],
                              background: FIT_BG[p.clementineFitScore],
                            }}>{p.clementineFitScore}</span>
                          ) : (
                            <span className="muted" style={{ fontSize: 11 }}>—</span>
                          )}
                        </td>
                        <td>
                          {p.companiesInScope != null && (
                            <span className="row muted" style={{ gap: 3, fontSize: 11.5 }}>
                              <Building2 size={11} />{p.companiesInScope}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {openProspect && (
        <ProspectDetailDrawer prospect={openProspect} onClose={() => setOpenProspect(null)} />
      )}
    </>
  )
}

// ─── LeadsPage ────────────────────────────────────────────────────────────────

export default function LeadsPage() {
  const [tab, setTab] = useState<'pipeline' | 'search'>('pipeline')

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="page-eyebrow">Leads</div>
          <h1 className="page-title">Leads</h1>
          <p className="page-subtitle">Manage your prospect pipeline and generate new leads via Roaring.io.</p>
        </div>
      </div>

      <div className="row" style={{ gap: 4, padding: 3, background: 'var(--ns-surface-2)', border: '1px solid var(--ns-border)', borderRadius: 8, width: 'fit-content', marginBottom: 20 }}>
        {([
          ['pipeline', 'Open Pipeline'],
          ['search',   'Prospect Generation'],
        ] as const).map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} style={{
            padding: '5px 14px', borderRadius: 5, border: 'none',
            background: tab === id ? 'var(--ns-surface)' : 'transparent',
            boxShadow:  tab === id ? 'var(--ns-shadow-sm)' : 'none',
            fontSize: 12.5, fontWeight: 500, cursor: 'pointer',
            color: tab === id ? 'var(--ns-text)' : 'var(--ns-text-muted)',
          }}>{label}</button>
        ))}
      </div>

      {tab === 'pipeline' && <PipelineTab />}
      {tab === 'search'   && <SearchTab />}
    </div>
  )
}
